import React, { useState, useEffect } from 'react';
import { useField } from 'formik';

import Checkbox from '@atoms/Checkbox';
import FormFieldsAddress from '@organisms/FormFieldsAddress';
import { Instructions, SmallInstructions } from '@styled';

const EditProfileFormAddresses = () => {
    const [mainPrefMailingField, mainMMeta, mainPrefMailingHelpers] = useField('mainAddressPreferredMailing');
    const [mainPrefBillingField, mainBMeta, mainPrefBillingHelpers] = useField('mainAddressPreferredBilling');
    const [altPrefMailingField, altMMeta, altPrefMailingHelpers] = useField('alternateAddressPreferredMailing');
    const [altPrefBillingField, altBMeta, altPrefBillingHelpers] = useField('alternateAddressPreferredBilling');
    const [homePrefMailingField, homeMMeta, homePrefMailingHelpers] = useField('homeAddressPreferredMailing');
    const [homePrefBillingField, homeBMeta, homePrefBillingHelpers] = useField('homeAddressPreferredBilling');

    const [altMailingDisabled, setAltMailingDisabled] = useState(false);
    const [homeMailingDisabled, setHomeMailingDisabled] = useState(false);
    const [altBillingDisabled, setAltBillingDisabled] = useState(false);
    const [homeBillingDisabled, setHomeBillingDisabled] = useState(false);

    const selectDefaultMailingPref = () => {
        if (!altPrefMailingField.value && !homePrefMailingField.value) {
            mainPrefMailingHelpers.setValue(true);
        }
    };
    const selectDefaultBillingPref = () => {
        if (!altPrefBillingField.value && !homePrefBillingField.value) {
            mainPrefBillingHelpers.setValue(true);
        }
    };

    // main address is required as pref mailing OR pref billing
    // If another address is selected for a pref, toggle off for other addresses AND
    // If another address is selected as mailing pref, disable billing pref for other addresses
    // If another address is selected billing pref, disable mailing pref for other addresses
    // reset if main pref for mailing or billing is selected

    // main - mailing
    useEffect(() => {
        if (mainPrefMailingField.value === true) {
            altPrefMailingHelpers.setValue(false);
            homePrefMailingHelpers.setValue(false);

            // disable other addresses billing pref
            setAltBillingDisabled(false);
            setHomeBillingDisabled(false);
        }
        selectDefaultMailingPref();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainPrefMailingField.value]);

    // main - billing
    useEffect(() => {
        if (mainPrefBillingField.value === true) {
            altPrefBillingHelpers.setValue(false);
            homePrefBillingHelpers.setValue(false);

            // enable other addresses mailing pref
            setAltMailingDisabled(false);
            setHomeMailingDisabled(false);
        }
        selectDefaultBillingPref();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainPrefBillingField.value]);

    // alt - mailing
    useEffect(() => {
        //  toggle disabled on other addresses for billing pref
        if (altPrefMailingField.value === true) {
            setAltBillingDisabled(true);
            setHomeBillingDisabled(true);
        }

        // make sure only one address is selected as mailing pref
        if (altPrefMailingField.value === true) {
            homePrefMailingHelpers.setValue(false);
            mainPrefMailingHelpers.setValue(false);
        }
        selectDefaultMailingPref();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [altPrefMailingField.value]);

    // alt - billing
    useEffect(() => {
        // toggle disabled on other addresses for mailing pref
        if (altPrefBillingField.value === true) {
            setAltMailingDisabled(true);
            setHomeMailingDisabled(true);
        }

        // make sure only one address is selected as billing pref
        if (altPrefBillingField.value === true) {
            homePrefBillingHelpers.setValue(false);
            mainPrefBillingHelpers.setValue(false);
        }
        selectDefaultBillingPref();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [altPrefBillingField.value]);

    // home - mailing
    useEffect(() => {
        // toggle disabled on other addresses for billing pref
        if (homePrefMailingField.value === true) {
            setAltBillingDisabled(true);
            setHomeBillingDisabled(true);
        }

        // make sure only one address is selected as mailing pref
        if (homePrefMailingField.value === true) {
            altPrefMailingHelpers.setValue(false);
            mainPrefMailingHelpers.setValue(false);
        }
        selectDefaultMailingPref();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [homePrefMailingField.value]);

    // home - billing
    useEffect(() => {
        // toggle disabled on other addresses for mailing pref
        if (homePrefBillingField.value === true) {
            setAltMailingDisabled(true);
            setHomeMailingDisabled(true);
        }

        // make sure only one address is selected as billing pref
        if (homePrefBillingField.value === true) {
            altPrefBillingHelpers.setValue(false);
            mainPrefBillingHelpers.setValue(false);
        }
        selectDefaultBillingPref();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [homePrefBillingField.value]);

    return (
        <>
            <div>
                <h5 className="mt-5">Personal Address (main)</h5>
                <Instructions>
                    In the event you change companies and/or we are unsuccessful reaching your business address, we will
                    contact you at this address. We will not use this information as your primary contact information
                    unless you specify otherwise.
                </Instructions>
                <div className="mb-4">
                    <SmallInstructions>
                        The main address must be either your mailing or billing address.
                    </SmallInstructions>
                    <Checkbox name="mainAddressPreferredMailing" label="Use this address for mailing purposes" />
                    <Checkbox name="mainAddressPreferredBilling" label="Use this address for billing purposes" />
                </div>
                <FormFieldsAddress
                    isRequired
                    fieldNameCountry="mainAddressCountry"
                    fieldNameAddress1="mainAddressAddress1"
                    fieldNameAddress2="mainAddressAddress2"
                    fieldNameAddress3="mainAddressAddress3"
                    fieldNameCity="mainAddressCity"
                    fieldNameState="mainAddressState"
                    fieldNameZip="mainAddressZip"
                />
            </div>

            <div>
                <h5 className="mb-4 mt-5">Personal Address (alternate)</h5>
                <div className="mb-3">
                    <SmallInstructions>
                        The main address must be either your mailing or billing address.
                    </SmallInstructions>
                    <Checkbox
                        disabled={altMailingDisabled}
                        name="alternateAddressPreferredMailing"
                        label="Use this address for mailing purposes"
                    />
                    <Checkbox
                        disabled={altBillingDisabled}
                        name="alternateAddressPreferredBilling"
                        label="Use this address for billing purposes"
                    />
                </div>
                <FormFieldsAddress
                    isRequired={altPrefMailingField.value || altPrefBillingField.value}
                    fieldNameCountry="alternateAddressCountry"
                    fieldNameAddress1="alternateAddressAddress1"
                    fieldNameAddress2="alternateAddressAddress2"
                    fieldNameAddress3="alternateAddressAddress3"
                    fieldNameCity="alternateAddressCity"
                    fieldNameState="alternateAddressState"
                    fieldNameZip="alternateAddressZip"
                />
            </div>

            <div>
                <h5 className="mb-4 mt-5">Personal Address (home)</h5>
                <div className="mb-3">
                    <SmallInstructions>
                        The main address must be either your mailing or billing address.
                    </SmallInstructions>
                    <Checkbox
                        disabled={homeMailingDisabled}
                        name="homeAddressPreferredMailing"
                        label="Use this address for mailing purposes"
                    />
                    <Checkbox
                        disabled={homeBillingDisabled}
                        name="homeAddressPreferredBilling"
                        label="Use this address for billing purposes"
                    />
                </div>
                <FormFieldsAddress
                    isRequired={homePrefMailingField.value || homePrefBillingField.value}
                    fieldNameCountry="homeAddressCountry"
                    fieldNameAddress1="homeAddressAddress1"
                    fieldNameAddress2="homeAddressAddress2"
                    fieldNameAddress3="homeAddressAddress3"
                    fieldNameCity="homeAddressCity"
                    fieldNameState="homeAddressState"
                    fieldNameZip="homeAddressZip"
                />
            </div>

            <div>
                <h5 className="mb-4 mt-5">Personal Address Options</h5>
                <Checkbox name="hideAddressFields" label="Hide address on profile" />
            </div>
        </>
    );
};

export default EditProfileFormAddresses;
